import { defer } from "lodash";
import { max } from "lodash";
import moment from "moment";
import { MouseEvent, useEffect, useState } from "react";
import { useRef } from "react";

import { hourInPixels, topPixelMargin } from "@apps/calendar/calendar";
import MeetingDialog from "@apps/meeting-dialog/meeting-dialog";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";

import Event from "./event";
import { OverlappingEvents } from "./utils";

const DayColumn = ({
  selectedDay,
  events,
  day,
  onClickMeetingLink,
}: {
  selectedDay: any;
  events: any;
  day: any;
  onClickMeetingLink: (meetingId: number, meetingGroupId: number) => void;
}) => {
  const [now, setNow] = useState(moment().format());
  const containerRef = useRef<HTMLDivElement>(null);
  const [time, setTime] = useState({ hours: moment().hour(), minutes: 0 });
  const [showCreateMeetingModal, setShowCreateMeetingModal] = useState(false);
  const overlappingEvents = new OverlappingEvents(events);
  const augmentedEvents = overlappingEvents.getAugmentedEvents();

  const handleClickCreateMeeting = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === containerRef.current) {
      const { clientY } = e;
      const { parentElement } = e.target as HTMLDivElement;
      const top =
        assertNonNull(parentElement?.getBoundingClientRect().top) +
        topPixelMargin;
      const pixelY = max([clientY - top, 0]) || 0;
      const hours = Math.floor(pixelY / hourInPixels);
      const minutes = (pixelY / hourInPixels - hours) * 60;
      setTime({ hours, minutes: minutes >= 30 ? 30 : 0 });
      defer(() => {
        setShowCreateMeetingModal(true);
      });
    }
  };

  const handleCloseDialog = () => setShowCreateMeetingModal(false);

  const date = moment(day)
    .hour(time.hours)
    .startOf("hour")
    .minute(time.minutes);
  const beforeSelectedDay = moment(selectedDay).subtract(1, "day");
  const afterSelectedDay = moment(selectedDay).add(1, "day");
  const formOptions = {
    startDatetime: date.clone().format(),
    endDatetime: date.clone().add(30, "minutes").format(),
  };

  const todayLineY = date.clone().isSame(moment(), "day")
    ? topPixelMargin +
      moment(now).hours() * hourInPixels +
      (moment(now).minutes() / 60) * hourInPixels
    : 0;

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(moment().format());
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className={classNames(
        `mt-px relative flex-col`,
        date.isSame(selectedDay, "day")
          ? "flex flex-col"
          : date.isSame(beforeSelectedDay, "day") ||
              date.isSame(afterSelectedDay, "day")
            ? "hidden @lg/calendar:flex flex-col"
            : "hidden @4xl/calendar:flex flex-col",
      )}
      onClick={handleClickCreateMeeting}
      ref={containerRef}
      aria-label="Weekly column container"
    >
      {showCreateMeetingModal && (
        <MeetingDialog onClose={handleCloseDialog} formOptions={formOptions} />
      )}

      {todayLineY > 0 && (
        <div
          className="absolute left-0 right-0 h-0.5 bg-red-500 z-1 pointer-events-none"
          style={{ top: `${todayLineY}px` }}
        />
      )}
      {augmentedEvents.map((augmentedEvent) => (
        <Event
          event={augmentedEvent}
          key={augmentedEvent.id}
          width={`${augmentedEvent.width}%`}
          left={`${augmentedEvent.left}%`}
          onClickMeetingLink={onClickMeetingLink}
        />
      ))}
    </div>
  );
};

export default DayColumn;
